import type {
  Abi,
  AbiStateMutability,
  Address,
  ContractFunctionArgs,
  Narrow,
  PublicClient,
} from "viem"

import type { SupportedChainId } from "../config"
import type {
  ContractCallParams,
  ContractFunctionName,
  ContractFunctionResult,
  InferFunctionName,
} from "./types"

const getBlockNumberAbi: Abi = [
  {
    inputs: [],
    name: "getBlockNumber",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
]

/**
 * Represents a utility class for making batched contract calls using the Multicall contract.
 */
export class MultiCallAggregator {
  /**
   * The PublicClient instance to use for making multicall requests.
   */
  client: PublicClient

  /**
   * An array of pending multicall contracts to execute.
   */
  pendingCalls: ContractCallParams[] = []

  /**
   * An array of pending resolve functions for multicall requests.
   */
  pendingResolves: ((arg: any) => void)[] = []

  /**
   * An array of pending reject functions for multicall requests.
   */
  pendingRejects: ((arg: unknown) => void)[] = []

  /**
   * A timer to schedule multicall execution.
   */
  timer: ReturnType<typeof setTimeout> | undefined = undefined

  /**
   * The maximum number of calls to include in a single batch multicall.
   */
  maxCallsInOneBatch: number

  /**
   * The chain ID associated with the client.
   */
  chainId: SupportedChainId

  /**
   * Creates a new MultiCallAggregator instance.
   * @param {PublicClient} client - The PublicClient instance to use for multicall requests.
   * @param {number} maxCallsInOneBatch - The maximum number of calls to include in a single batch multicall.
   */
  constructor(client: PublicClient, maxCallsInOneBatch?: number) {
    this.client = client
    this.maxCallsInOneBatch = maxCallsInOneBatch || 0
    this.chainId = client.chain?.id as SupportedChainId
  }

  /**
   * Makes a batched contract call using multicall.
   * @param {Address} address - The contract address.
   * @param {Abi} abi - The contract ABI.
   * @param {string} functionName - The contract function name to call.
   * @param {unknown[]} args - An array of function arguments.
   * @param {boolean} makeMulticallIfExceeds - If true, make a multicall if the maximum batch size exceeds.
   * @returns {Promise<{ blockNumber: number; returnValue: ContractFunctionResult }>} - A promise that resolves to the result of the contract call.
   */
  async call<
    abi extends Abi | readonly unknown[] = Abi,
    mutability extends AbiStateMutability = AbiStateMutability,
    functionName extends ContractFunctionName<
      abi,
      mutability
    > = ContractFunctionName<abi, mutability>,
    args extends ContractFunctionArgs<
      abi,
      mutability,
      functionName
    > = ContractFunctionArgs<abi, mutability, functionName>,
  >(
    address: Address,
    abi: Narrow<abi>,
    functionName: InferFunctionName<abi, functionName, mutability>,
    args?: args,
    makeMulticallIfExceeds = true,
  ): Promise<{
    blockNumber: number
    returnValue: ContractFunctionResult<abi, mutability, functionName, args>
  }> {
    this.client.readContract
    this.sheduleMulticall()
    this.pendingCalls.push({
      address,
      abi,
      functionName,
      args,
    } as ContractCallParams)
    const promise = new Promise<{
      blockNumber: number
      returnValue: ContractFunctionResult<abi, mutability, functionName, args>
    }>((resolve, reject) => {
      this.pendingResolves.push(resolve)
      this.pendingRejects.push(reject)
    })
    if (makeMulticallIfExceeds) this.makeMulticallIfMaxBatchSizeExceeds()
    return promise
  }

  /**
   * Makes a batched contract call using multicall and returns the contract function result.
   * @param {Address} address - The contract address.
   * @param {Abi} abi - The contract ABI.
   * @param {string} functionName - The contract function name to call.
   * @param {unknown[]} args - An array of function arguments.
   * @returns {Promise<ContractFunctionResult>} - A promise that resolves to the result of the contract call.
   */
  async callValue<
    abi extends Abi | readonly unknown[] = Abi,
    mutability extends AbiStateMutability = AbiStateMutability,
    functionName extends ContractFunctionName<
      abi,
      mutability
    > = ContractFunctionName<abi, mutability>,
    args extends ContractFunctionArgs<
      abi,
      mutability,
      functionName
    > = ContractFunctionArgs<abi, mutability, functionName>,
  >(
    address: Address,
    abi: Narrow<abi>,
    functionName: InferFunctionName<abi, functionName, mutability>,
    args: args,
  ): Promise<ContractFunctionResult<abi, mutability, functionName, args>> {
    this.sheduleMulticall()
    this.pendingCalls.push({
      address,
      abi,
      functionName,
      args,
    } as ContractCallParams)
    const promise = new Promise<{
      blockNumber: number
      returnValue: ContractFunctionResult<abi, mutability, functionName, args>
    }>((resolve, reject) => {
      this.pendingResolves.push(resolve)
      this.pendingRejects.push(reject)
    })
    this.makeMulticallIfMaxBatchSizeExceeds()
    const res = await promise
    return res.returnValue
  }

  /**
   * Makes a batched contract call using multicall for multiple contract functions in the same block.
   * @param {Address} address - The contract address.
   * @param {Abi} abi - The contract ABI.
   * @param {Array<[string, unknown[] | undefined]>} functions - An array of function names and their arguments.
   * @returns {Promise<{ blockNumber: number; returnValues: unknown[] }>} - A promise that resolves to the results of the contract calls.
   */
  async callContractSameBlock(
    address: Address,
    abi: Abi,
    functions: [string, unknown[] | undefined][],
  ): Promise<{ blockNumber: number; returnValues: unknown[] }> {
    if (functions.length === 0) return { blockNumber: -1, returnValues: [] }
    this.sheduleMulticall()
    const promise = Promise.all(
      functions.map(([name, args]) =>
        this.call(address, abi, name, args, false),
      ),
    )
    this.makeMulticallIfMaxBatchSizeExceeds()
    const res = await promise
    return {
      blockNumber: res[0]?.blockNumber || 0,
      returnValues: res.map(({ returnValue }) => returnValue),
    }
  }

  /**
   * Makes a batched contract call using multicall for multiple contract functions.
   * @param {ContractCallParams<abi, mutability, functionName, args>[]} calls - An array of contract calls to execute.
   * @returns {Promise<{ blockNumber: number; returnValues: unknown[] }>} - A promise that resolves to the results of the contract calls.
   */
  async callSameBlock<
    abi extends Abi | readonly unknown[] = Abi,
    mutability extends AbiStateMutability = AbiStateMutability,
    functionName extends ContractFunctionName<
      abi,
      mutability
    > = ContractFunctionName<abi, mutability>,
    args extends ContractFunctionArgs<
      abi,
      mutability,
      functionName
    > = ContractFunctionArgs<abi, mutability, functionName>,
  >(
    calls: ContractCallParams<abi, mutability, functionName, args>[],
  ): Promise<{ blockNumber: number; returnValues: unknown[] }> {
    if (calls.length === 0) return { blockNumber: -1, returnValues: [] }
    this.sheduleMulticall()
    const promise = Promise.all(
      calls.map(({ address, abi, functionName, args }) =>
        this.call(address, abi, functionName, args, false),
      ),
    )
    this.makeMulticallIfMaxBatchSizeExceeds()
    const res = await promise
    return {
      blockNumber: res[0]?.blockNumber || 0,
      returnValues: res.map(({ returnValue }) => returnValue),
    }
  }

  /**
   * Schedules a multicall to be executed.
   */
  async sheduleMulticall() {
    if (this.timer === undefined) {
      this.timer = setTimeout(async () => {
        this.timer = undefined
        await this.makeMulticallNow()
      }, 0)
    }
  }

  /**
   * Makes a multicall immediately if the maximum batch size exceeds.
   * @returns {Promise<void>} - A promise that resolves when the multicall is made.
   */
  async makeMulticallIfMaxBatchSizeExceeds(): Promise<void> {
    if (
      this.maxCallsInOneBatch === 0 ||
      this.pendingCalls.length < this.maxCallsInOneBatch
    )
      return
    await this.makeMulticallNow()
  }

  /**
   * Makes a multicall immediately.
   * @returns {Promise<void>} - A promise that resolves when the multicall is made.
   */
  async makeMulticallNow(): Promise<void> {
    const pendingCalls = this.pendingCalls
    const pendingResolves = this.pendingResolves
    const pendingRejects = this.pendingRejects
    this.pendingCalls = []
    this.pendingResolves = []
    this.pendingRejects = []
    if (pendingCalls.length === 0) return
    pendingCalls.unshift({
      address: this.getBlockNumberContractAddress(),
      abi: getBlockNumberAbi,
      functionName: "getBlockNumber",
    })
    let res
    for (;;) {
      try {
        res = await this.client.multicall({
          allowFailure: true,
          contracts: pendingCalls.map((c) => ({
            address: c["address"],
            abi: c["abi"],
            functionName: c["functionName"],
            args: (c["args"] as Narrow<readonly unknown[] | undefined>) || [],
          })),
        })
      } catch (e) {
        console.warn(this.client.chain?.id, `Multicall error ${e}`)
        continue
      }
      break
    }
    if (res[0]?.status !== "success") {
      // getBlockNumber Failed
      for (let i = 1; i < res.length; ++i)
        pendingRejects?.[i - 1]?.(res[0]?.error)
    } else {
      const blockNumber = res[0].result as number
      for (let i = 1; i < res.length; ++i) {
        if (res[i]?.status === "success")
          pendingResolves[i - 1]?.({ blockNumber, returnValue: res[i]?.result })
        else pendingRejects[i - 1]?.(res[i]?.error)
      }
    }
  }

  /**
   * Gets the contract address of the Multicall contract for the current chain.
   * @returns {Address} - The contract address.
   */
  getBlockNumberContractAddress(): Address {
    return this.client.chain?.contracts?.multicall3?.address as Address
  }
}
